import type { CSSProperties } from 'react';
import { RADIUS_XL } from './radius';

/**
 * Liquid Glass surfaces (Figma node 89519-22559).
 *
 * A glass surface is painted in two parts, so each variant ships both:
 *
 *   `fill`   — goes on the element itself
 *   `effect` — the inner sheen; goes on a ::before/::after overlay that fills
 *              the element, because it must sit above the fill but below the
 *              content
 *
 * `tinted` adds two more layers under the fill (`backing`, `tintLayer`).
 *
 * PLATFORM CAVEATS — read before shipping a surface:
 *  - `plus-darker` / `plus-lighter` are Apple-only blend modes. Android
 *    WebView ignores them, so `tinted` and `regular` degrade there. Gate on
 *    a capability check and fall back to a solid surface.
 *  - Figma specifies no `backdrop-filter`; the blur radius is still TBD from
 *    design/native. Nothing here sets one.
 */

export type TGlassSurface = {
  /** Every glass surface is 32px. */
  radius: string;
  /** Base layer — apply to the element. */
  fill: CSSProperties;
  /** Inner sheen — apply to a full-bleed ::after overlay. */
  effect: CSSProperties;
  /** `tinted` only: opaque white sheet painted underneath everything. */
  backing?: CSSProperties;
  /** `tinted` only: brand tint painted over the backing. */
  tintLayer?: CSSProperties;
};

export type TGlassSurfaces = {
  /** The brand colour the tint is drawn from (= COLOR_PRIMARY_MAIN). */
  tint: string;
  tinted: TGlassSurface;
  regular: TGlassSurface;
  clear: TGlassSurface;
};

/** Shared inner sheen; only the two edge stops differ between variants. */
const sheen = (edge: string): CSSProperties => ({
  background: 'rgba(0, 0, 0, 0.004)',
  boxShadow: [
    `inset 0 40px 10px -40px ${edge}`,
    `inset 0 -40px 10px -40px ${edge}`,
    'inset 0 40px 30px -40px #E6E6E6'
  ].join(', ')
});

/**
 * Builds the glass set for one partner.
 *
 * @param tint the partner's COLOR_PRIMARY_MAIN — the only per-partner input.
 */
export const buildGlassSurfaces = (tint: string): TGlassSurfaces => ({
  tint,

  tinted: {
    radius: RADIUS_XL,
    backing: { background: '#FFFFFF', opacity: 0.94 },
    tintLayer: { background: tint, mixBlendMode: 'plus-darker' as never },
    fill: { background: 'rgba(0, 0, 0, 0.004)' },
    effect: sheen('#282828')
  },

  regular: {
    radius: RADIUS_XL,
    fill: {
      background: [
        'linear-gradient(0deg, rgba(248, 248, 248, 0.2), rgba(248, 248, 248, 0.2))',
        'linear-gradient(0deg, rgba(68, 68, 68, 0.6), rgba(68, 68, 68, 0.6))',
        'linear-gradient(0deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.25))',
        'rgba(0, 0, 0, 0.25)'
      ].join(', '),
      backgroundBlendMode: 'luminosity, plus-lighter, normal, normal',
      boxShadow: [
        '1.25px 0 0 -0.75px #D0D0D0',
        '-1.25px 0 0 -0.75px #D0D0D0',
        '0 0 0 0.5px #E8E8E8',
        '0 8px 15px rgba(0, 0, 0, 0.02)'
      ].join(', ')
    },
    effect: sheen('#282828')
  },

  clear: {
    radius: RADIUS_XL,
    fill: {
      background: 'rgba(153, 153, 153, 0.17)',
      backgroundBlendMode: 'luminosity',
      boxShadow: [
        '1.25px 0 0 -0.75px #A6A6A6',
        '-1.25px 0 0 -0.75px #A6A6A6',
        '0 0 0 0.5px #A6A6A6',
        '0 8px 15px rgba(0, 0, 0, 0.04)'
      ].join(', ')
    },
    effect: sheen('#1A1A1A')
  }
});
